import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getUserCards, cardUse } from "../api/apis";
import { toast } from "react-toastify";
import { getFromLocalStorage, removeFromLocalStorage } from "../utils/localStorage";
import { Package, Clock, User, Tag, Play, Check, History, Trash2 } from "lucide-react";
import ProtectedRoute from "../Components/ProtectedRoute";
import axios from "axios";

const MyCards = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreatorModal, setShowCreatorModal] = useState(false);
  const [creators, setCreators] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [modalCard, setModalCard] = useState(null);
  const fetchedRef = useRef(false);
  const abortControllerRef = useRef(null);

  const token = getFromLocalStorage("token");
  const user = getFromLocalStorage("user");

  const checkTimeout = useCallback(() => {
    const expirationTime = getFromLocalStorage("expirationTime");
    if (!expirationTime || Date.now() > parseInt(expirationTime, 10)) {
      removeFromLocalStorage("token");
      removeFromLocalStorage("adminToken");
      removeFromLocalStorage("user");
      removeFromLocalStorage("expirationTime");
      return false;
    }
    return true;
  }, []);

  const handleLogout = useCallback(() => {
    removeFromLocalStorage("token");
    removeFromLocalStorage("adminToken");
    removeFromLocalStorage("user");
    navigate("/");
    window.location.reload();
  }, [navigate]);

  const getRarityColor = (rarity) => {
    switch (rarity?.toLowerCase()) {
      case 'common':
        return 'from-gray-500 to-gray-600';
      case 'rare':
        return 'from-blue-500 to-blue-600';
      case 'epic':
        return 'from-purple-500 to-purple-600';
      case 'legendary':
        return 'from-yellow-500 to-orange-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  // Normalize creator name for display
  const formatCreatorName = (name) => {
    if (!name) return '';
    // Convert all variations of admin to CREZAR
    const adminVariations = ['admin', 'Admin', 'ADMIN', 'Administrator', 'administrator'];
    return adminVariations.includes(name.trim()) ? 'CREZAR' : name;
  };

  const fetchCards = useCallback(async () => {
    if (fetchedRef.current) return;

    if (!checkTimeout()) {
      handleLogout();
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const token = getFromLocalStorage("token");
      const user = getFromLocalStorage("user");

      if (!token) {
        if (!window.__suppressAuthChecks) {
          toast.error("No token found. Please log in again.");
          navigate("/login");
        }
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user?.emailId || user?.username || user
        },
        signal: controller.signal
      };

      const response = await getUserCards(config);

      if (!controller.signal.aborted) {
        if (response?.data?.status === "success") {
          // Filter out dismissed cards (deleted from history)
          const activeCards = (response.data.cards || []).filter(card =>
            card.actionStatus !== 'dismissed'
          );
          setCards(activeCards);
        } else {
          setCards([]);
        }
        fetchedRef.current = true;
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.name !== 'CanceledError' && !controller.signal.aborted) {
        console.error('Error fetching cards:', error);
        if (error.response?.status === 401) {
          handleLogout();
        } else if (!error.message?.includes('canceled')) {
          toast.error('Failed to load cards. Please try again.');
        }
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [navigate, checkTimeout, handleLogout]);

  useEffect(() => {
    if (fetchedRef.current) return;

    const timeoutId = setTimeout(() => {
      fetchCards();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchCards]);

  const handleUseCard = async (card) => {
    if (!card || !card._id || !card.transactionId) {
      toast.error("Invalid card data. Please try again.");
      return;
    }

    const formData = {
      cardId: card._id,
      transactionId: card.transactionId,
      cardTitle: card.cardTitle || "",
      description: card.description || "",
      streamerEmailId: card.streamerEmailId || "",
      duration: card.duration || 10
    };

    try {
      const response = await cardUse(formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user?.emailId || user?.username || user,
        },
      });

      if (response.data.status === "success") {
        const message = response.data.message;
        const duration = 10;

        setCards(prevCards =>
          prevCards.map((c) => {
            if (!c) return null;
            return c.transactionId === card.transactionId
              ? {
                ...c,
                isUsed: true,
                cardTitle: card.cardTitle || c.cardTitle,
                description: card.description || c.description,
                duration: duration
              }
              : c;
          }).filter(Boolean)
        );
        toast.success(message || "Card used successfully!");
      } else {
        toast.error(response.data.error || "Card use failed!");
      }
    } catch (error) {
      toast.error("An unexpected error occurred while using the card.");
    }
  };

  const handleOpenModal = async (card) => {
    setSelectedCard(card);

    // Fetch creators for selection
    try {
      const token = getFromLocalStorage("token");
      const user = getFromLocalStorage("user");

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user?.emailId || user?.username || user
        }
      };

      // Use getStreamers instead of getAllStreamers (same endpoint as Streamers page)
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/auth/user/getStreamers`, config);
      console.log('Streamers API response:', response);

      if (response?.data?.status === "success" || response?.data?.streamers) {
        const streamersList = response.data.streamers || [];
        // IMPORTANT: Only show Pro creators (premium feature)
        const proStreamers = streamersList.filter(streamer =>
          streamer.membershipPlan === 'pro' || streamer.isPro === true
        );
        console.log('Pro streamers list:', proStreamers);
        setCreators(proStreamers);
      } else if (Array.isArray(response.data)) {
        console.log('Streamers array:', response.data);
        // Filter for Pro users even when response is a direct array
        const proStreamers = response.data.filter(streamer =>
          streamer.membershipPlan === 'pro' || streamer.isPro === true
        );
        setCreators(proStreamers);
      } else {
        console.log('Unexpected response format:', response?.data);
        setCreators([]);
      }
    } catch (error) {
      console.error('Error fetching creators:', error);
      toast.error('Failed to load creators. Please try again.');
    }

    // Skip customization modal, go directly to creator selection
    setShowCreatorModal(true);
  };

  const handleCloseModal = () => {
    setSelectedCard(null);
    setShowModal(false);
    setShowCreatorModal(false);
  };

  const handleOpenCardModal = (card) => {
    setModalCard(card);
  };

  const handleCloseCardModal = () => {
    setModalCard(null);
  };

  const handleSendChallenge = () => {
    if (selectedCard && selectedCreator) {
      const updatedCard = {
        ...selectedCard,
        streamerEmailId: selectedCreator.emailId || selectedCreator.email
      };
      handleUseCard(updatedCard);
      setShowCreatorModal(false);
      setSelectedCreator(null);
      handleCloseModal();
    } else {
      toast.error("Please select a creator to send the challenge to.");
    }
  };

  // Delete individual used card
  const handleDeleteUsedCard = async (card) => {
    if (!card || !card.transactionId) {
      toast.error("Invalid card data");
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to delete "${card.cardTitle}" from your history?`);
    if (!confirmed) return;

    try {
      const token = getFromLocalStorage("token");
      const user = getFromLocalStorage("user");

      // Mark transaction as dismissed (hidden) instead of deleting
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/auth/user/purchased-cards/${card.transactionId}/action`,
        { action: 'dismissed' },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Info": user?.emailId || user?.username || user
          }
        }
      );

      // Remove from local state
      setCards(prevCards => prevCards.filter(c => c.transactionId !== card.transactionId));
      toast.success("Card deleted from history");
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error("Failed to delete card");
    }
  };

  // Clear all used cards
  const handleClearAllUsed = async () => {
    if (usedCards.length === 0) {
      toast.info("No used cards to clear");
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to clear all ${usedCards.length} used cards from your history?`);
    if (!confirmed) return;

    try {
      const token = getFromLocalStorage("token");
      const user = getFromLocalStorage("user");

      // Mark all used cards as dismissed in parallel
      const dismissPromises = usedCards.map(card =>
        axios.post(
          `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/auth/user/purchased-cards/${card.transactionId}/action`,
          { action: 'dismissed' },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "User-Info": user?.emailId || user?.username || user
            }
          }
        ).catch(err => console.warn('Failed to dismiss card:', card.transactionId, err))
      );

      await Promise.allSettled(dismissPromises);

      // Remove all used cards from local state
      setCards(prevCards => prevCards.filter(c => !c.isUsed));
      toast.success(`Cleared ${usedCards.length} used cards`);
    } catch (error) {
      console.error('Error clearing used cards:', error);
      toast.error("Failed to clear all cards");
    }
  };

  // Treat any purchased card (has a transactionId) as used.
  // Filter cards properly - used cards are marked as isUsed, available cards are not used
  const usedCards = cards.filter(card => card && card.isUsed);
  const availableCards = cards.filter(card => card && !card.isUsed);

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <main className="pt-6 pb-12 px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                    My Cards
                  </h1>
                  <p className="text-gray-400">Manage and use your card collection</p>
                </div>
              </div>
            </div>

            {/* Available Cards Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
                <Package className="w-6 h-6 text-yellow-400" />
                <span>Available Cards</span>
              </h2>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {availableCards.length === 0 ? (
                    <div className="col-span-full text-center py-20">
                      <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Package className="w-12 h-12 text-gray-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-400 mb-2">No cards available</h3>
                      <p className="text-gray-500">Purchase some cards to get started!</p>
                    </div>
                  ) : (
                    availableCards.map((card) => (
                      <div
                        key={card.transactionId || card._id}
                        className="bg-black/30 rounded-xl p-6 border border-gray-700/50 backdrop-blur-sm hover:border-yellow-500/50 transition-all duration-300 hover:transform hover:scale-105 cursor-pointer"
                        onClick={() => handleOpenCardModal(card)}
                      >
                        {/* Card Image */}
                        <div className="relative mb-4 overflow-hidden rounded-lg">
                          <img
                            src={card.imageUrl || "https://via.placeholder.com/300x200"}
                            alt={card.cardTitle}
                            className="w-full h-40 object-contain bg-black transition-transform duration-300 group-hover:scale-105"
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/300x200";
                            }}
                          />
                          <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${getRarityColor(card.rarity)} text-white`}>
                            {card.rarity}
                          </div>
                        </div>

                        {/* Card Info */}
                        <div className="space-y-3">
                          <h3 className="text-lg font-bold text-white">
                            {card.cardTitle}
                          </h3>
                          <p className="text-gray-400 text-sm line-clamp-2">
                            {card.description}
                          </p>




                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenModal(card);
                            }}
                            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200 flex items-center justify-center space-x-2"
                          >
                            <Play className="w-4 h-4" />
                            <span>Send Challenge</span>
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Used Cards Section */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
                  <History className="w-6 h-6 text-gray-400" />
                  <span>Used Cards</span>
                </h2>
                {usedCards.length > 0 && (
                  <button
                    onClick={handleClearAllUsed}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 hover:text-red-300 transition-all text-sm font-medium"
                    title="Clear all used cards"
                  >
                    <Trash2 className="w-4 h-4" />
                    Clear All
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {usedCards.length === 0 ? (
                  <div className="col-span-full text-center py-20">
                    <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <History className="w-12 h-12 text-gray-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-400 mb-2">No used cards yet</h3>
                    <p className="text-gray-500">Cards you use will appear here</p>
                  </div>
                ) : (
                  usedCards.map((card) => (
                    <div
                      key={card.transactionId || card._id}
                      className="bg-black/20 rounded-xl p-6 border border-gray-700/30 backdrop-blur-sm opacity-75 hover:border-yellow-500/50 transition-all duration-300 hover:transform hover:scale-105 cursor-pointer"
                      onClick={() => handleOpenCardModal(card)}
                    >
                      {/* Card Image */}
                      <div className="relative mb-4 overflow-hidden rounded-lg">
                        <img
                          src={card.imageUrl || "https://via.placeholder.com/300x200"}
                          alt={card.cardTitle}
                          className="w-full h-40 object-contain bg-black grayscale"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/300x200";
                          }}
                        />
                        <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold bg-gray-600 text-gray-300">
                          {card.rarity}
                        </div>
                        <div className="absolute top-3 right-3 bg-green-500/90 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                          <Check className="w-3 h-3" />
                          <span>USED</span>
                        </div>
                      </div>

                      {/* Card Info */}
                      <div className="space-y-3">
                        <h3 className="text-lg font-bold text-gray-300">
                          {card.cardTitle}
                        </h3>
                        <p className="text-gray-500 text-sm line-clamp-2">
                          {card.description}
                        </p>

                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <User className="w-4 h-4" />
                          <span>{formatCreatorName(card.creatorName) || card.streamerEmailId}</span>
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteUsedCard(card);
                          }}
                          className="w-full bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 hover:text-red-300 font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 mt-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </main>


        {/* Creator Selection Modal */}
        {showCreatorModal && selectedCard && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-black/90 backdrop-blur-xl border border-gray-700 rounded-xl p-8 max-w-md mx-4 w-full">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-yellow-400" />
                </div>
                <h2 className="text-2xl font-bold text-white">Select Creator</h2>
                <p className="text-gray-400 mt-2">Choose which creator to send your challenge to</p>
              </div>

              <div className="space-y-4 max-h-60 overflow-y-auto">
                {creators.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No creators available</p>
                  </div>
                ) : (
                  creators.map((creator, index) => (
                    <div
                      key={creator._id || index}
                      onClick={() => setSelectedCreator(creator)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${selectedCreator?._id === creator._id
                        ? 'border-yellow-500 bg-yellow-500/10'
                        : 'border-gray-600 bg-black/30 hover:border-yellow-500/50'
                        }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-black" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{formatCreatorName(creator.username || creator.name)}</h3>
                          <p className="text-gray-400 text-sm">{creator.emailId || creator.email}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex space-x-4 mt-8">
                <button
                  onClick={handleSendChallenge}
                  disabled={!selectedCreator}
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Play className="w-4 h-4" />
                  <span>Send Challenge</span>
                </button>
                <button
                  onClick={() => {
                    setShowCreatorModal(false);
                    setSelectedCreator(null);
                  }}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Card Detail Modal */}
        {modalCard && (
          <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={handleCloseCardModal}>
            <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 border border-yellow-500/30 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <button className="absolute top-3 right-3 text-yellow-300 hover:text-yellow-200 text-2xl font-bold w-8 h-8 flex items-center justify-center" onClick={handleCloseCardModal}>
                ×
              </button>
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-80 md:h-full">
                  <img src={modalCard.imageUrl || "https://via.placeholder.com/300x200"} alt={modalCard.cardTitle} className="w-full h-full object-contain bg-black" />
                  <div className="absolute inset-0 ring-1 ring-yellow-400/40 rounded-none"></div>
                  <div className="absolute top-3 left-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${getRarityColor(modalCard.rarity)} text-white`}>
                      {modalCard.rarity}
                    </div>
                  </div>
                  <div className="absolute top-3 right-3 bg-green-500/90 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                    <Check className="w-3 h-3" />
                    <span>{modalCard.isUsed ? 'USED' : 'AVAILABLE'}</span>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <h3 className="text-2xl font-bold text-yellow-300">{modalCard.cardTitle}</h3>
                  <p className="text-gray-300 text-sm">{modalCard.description}</p>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-sm text-gray-400">
                      <User className="w-4 h-4 text-yellow-400" />
                      <span>{formatCreatorName(modalCard.creatorName) || formatCreatorName(modalCard.streamerEmailId)}</span>
                    </div>

                  </div>

                  {!modalCard.isUsed && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseCardModal();
                        handleOpenModal(modalCard);
                      }}
                      className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200 flex items-center justify-center space-x-2"
                    >
                      <Play className="w-4 h-4" />
                      <span>Send Challenge</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default MyCards;
